import i18nFactory from '@yandex-int/i18n';
import { includes, uniq } from 'lodash';
import { Epic, ofType } from 'redux-observable';
import { concat, empty, from, Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mapTo, switchMap } from 'rxjs/operators';

import { Action } from '../../actions';
import { changeLocation } from '../../actions/common';
import { resetGridData, updateDuplicateGroupsItemsData } from '../../actions/grid-screen';
import {
    ACCEPT_DUPLICATE_GROUPS_ITEMS,
    CHANGE_DUPLICATE_GROUPS_MODAL_VIEW_STATE,
    ChangeDuplicateGroupsModalViewState,
    closeDuplicateGroupsModal,
    FETCH_DUPLICATE_GROUPS_CAMPAIGN_SELECTOR_DATA_ITEMS,
    fetchDuplicateGroupsCampaignSelectorDataItems,
    FetchDuplicateGroupsCampaignSelectorDataItems,
    resetDuplicateGroupsCampaignSelectorState,
    setAcceptToDuplicateSelectedGroups,
    showErrorDuplicateGroupsCampaignSelectorDataItems,
    updateDuplicateGroupsCampaignSelectorDataItems
} from '../../actions/groupsDuplicateSelector';
import { getDuplicateSuccessMessage } from '../../service/DuplicateHelper/DuplicateHelper';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import { GroupsHelpers } from '../../service/GroupsHelpers/GroupsHelpers';
import '../../service/Rx';
import { EpicDeps } from '../../service/StoreFactory';
import { UrlHelpers } from '../../service/UrlHelpers';
import { CopyAdGroupsMutation } from '../../typings/graphql-grid';
import { NoticeType } from '../../typings/grid';
import { DuplicateRadioControlType } from '../../typings/groups';
import { State } from '../../typings/state';

import * as keyset from './groupsDuplicateSelector.i18n';

const DEBOUNCE_WAIT_TIME_TEXT_INPUT = 500;
const i18n = i18nFactory(keyset);

const fetchDuplicateGroupsCampaignSelectorDataItemsEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    const fetchAction$ = action$.pipe(
        ofType(FETCH_DUPLICATE_GROUPS_CAMPAIGN_SELECTOR_DATA_ITEMS),
        debounceTime(DEBOUNCE_WAIT_TIME_TEXT_INPUT),
        switchMap((action: FetchDuplicateGroupsCampaignSelectorDataItems) => {
            const state = store$.value;
            const { login } = state.commonData;

            return from(
                deps.entitiesDataProvider.fetchCampaignsSuggestListWithTextTypeFilter(
                    login,
                    action.textInput,
                    action.campaignType
                )
            ).pipe(
                map(updateDuplicateGroupsCampaignSelectorDataItems),
                catchError(() => {
                    return of(showErrorDuplicateGroupsCampaignSelectorDataItems());
                })
            );
        })
    );

    const resetAction$ = action$.pipe(
        ofType(FETCH_DUPLICATE_GROUPS_CAMPAIGN_SELECTOR_DATA_ITEMS),
        filter(() => {
            const { campaignItems } = store$.value.grid.duplicateGroupsData.campaignsSelector;

            return Boolean(campaignItems && campaignItems.length !== 0);
        }),
        mapTo(resetDuplicateGroupsCampaignSelectorState())
    );

    return resetAction$.merge(fetchAction$);
};

const changeDuplicateGroupsModalViewStateEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(CHANGE_DUPLICATE_GROUPS_MODAL_VIEW_STATE),
        map(
            (actionDuplication: ChangeDuplicateGroupsModalViewState): FetchDuplicateGroupsCampaignSelectorDataItems => {
                return fetchDuplicateGroupsCampaignSelectorDataItems('', actionDuplication.campaignType);
            }
        )
    );
};

const acceptDuplicateGroupsItemsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ACCEPT_DUPLICATE_GROUPS_ITEMS),
        switchMap(
            (): Observable<Action> => {
                const state = store$.value;
                const { login } = state.commonData;
                const { selectedCampaign, selectedRadioButtonType, items } = state.grid.duplicateGroupsData;
                let campaignId: null | string = null;

                if (selectedCampaign && selectedRadioButtonType === DuplicateRadioControlType.ANOTHER_CAMPAIGN) {
                    campaignId = selectedCampaign.id;
                }

                const availableItems = items.filter(item => !(item.isInvalid || item.isDisabled));
                const groupsIds = availableItems.map(item => item.groupId);

                const campaignsIds = uniq(availableItems.map(item => item.campaignId));

                return from(
                    deps.groupsMutationExecutor.duplicateGroups(
                        {
                            adGroupIds: groupsIds,
                            destinationCampaignId: campaignId
                        },
                        login
                    )
                ).pipe(
                    switchMap(
                        (response: CopyAdGroupsMutation): Observable<Action> => {
                            const {
                                copyAdGroups: { copiedAdGroupIds, validationResult }
                            } = response;
                            const copiedAdGroupIdsLength = copiedAdGroupIds.length;
                            if (validationResult && copiedAdGroupIdsLength === 0) {
                                const {
                                    copyErrorLimitIdxList,
                                    copyErrorsBrokenGroupsIdxList,
                                    contentPromotionErrors
                                } = GroupsHelpers.parseGroupsCopyMutationsError(validationResult);
                                const markedItems = items.map((item, i) => ({
                                    ...item,
                                    isInvalid: includes(copyErrorsBrokenGroupsIdxList, String(i)),
                                    isDisabled: includes(copyErrorLimitIdxList, String(i))
                                }));

                                return concat(
                                    of(
                                        setAcceptToDuplicateSelectedGroups(
                                            copyErrorLimitIdxList,
                                            copyErrorsBrokenGroupsIdxList,
                                            contentPromotionErrors
                                        )
                                    ),
                                    of(updateDuplicateGroupsItemsData(markedItems, true))
                                );
                            }

                            if (selectedRadioButtonType === DuplicateRadioControlType.ACTUAL_CAMPAIGN) {
                                showNoticeMessage(
                                    NoticeType.success,
                                    getDuplicateSuccessMessage(groupsIds, campaignsIds)
                                );

                                return concat(of(resetGridData()), of(closeDuplicateGroupsModal()));
                            } else if (
                                selectedCampaign &&
                                selectedRadioButtonType === DuplicateRadioControlType.ANOTHER_CAMPAIGN
                            ) {
                                showNoticeMessage(
                                    NoticeType.success,
                                    getDuplicateSuccessMessage(groupsIds, [selectedCampaign.id])
                                );

                                return concat(
                                    of(closeDuplicateGroupsModal()),
                                    of(resetGridData()),
                                    of(
                                        changeLocation(
                                            UrlHelpers.getGroupsListUrl(),
                                            {
                                                ulogin: login,
                                                'campaigns-ids': selectedCampaign.id
                                            },
                                            false
                                        )
                                    )
                                );
                            }

                            return empty();
                        }
                    ),
                    catchError(() => {
                        showNoticeMessage(NoticeType.error, i18n('fatalError'));

                        return of(closeDuplicateGroupsModal());
                    })
                );
            }
        )
    );
};

export {
    fetchDuplicateGroupsCampaignSelectorDataItemsEpic,
    changeDuplicateGroupsModalViewStateEpic,
    acceptDuplicateGroupsItemsEpic
};
